import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Field, Button } from "@/components/ui";
import Seo from "@/components/Seo";
import { ImmersiveShell } from "@/components/immersive/ImmersiveShell";
import { api } from "@/lib/api";

const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || "");

export default function Contact() {
  const nav = useNavigate();
  const [d, setD] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const canSend = d.name.trim() && emailValid(d.email) && d.phone.trim() && d.message.trim();

  const submit = async () => {
    if (!canSend || busy) return;
    setBusy(true); setErr(null);
    try {
      await api.post("/contact", { name: d.name.trim(), email: d.email.trim(), phone: d.phone.trim(), message: d.message.trim() }, false);
      setDone(true);
    } catch (e) {
      setErr(e?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ImmersiveShell>
      <Seo title="Contact" path="/contact" description="Get in touch with the HomecareMatches team. Send us your name, email, mobile and a note and we'll get back to you." />
      <section className="imm-wrap" data-testid="contact-screen" style={{ paddingBottom: 20 }}>
        <div className="imm-legal">
          <button className="imm-back" data-testid="contact-back" onClick={() => nav(-1)}><Icon name="arrow-left" size={16} /> Back</button>
          <h1>Contact us</h1>
          <p className="eff" style={{ marginTop: 6 }}>Tell us what you need and we'll get back to you at the email you provide.</p>

          {done ? (
            <div className="imm-contact-done" data-testid="contact-success" role="status" aria-live="polite">
              <Icon name="check-circle" size={40} color="var(--success)" />
              <h2>Message sent</h2>
              <p>Thanks, {d.name.split(" ")[0] || "there"} — your message is on its way to our support team. We'll reply to {d.email}.</p>
              <Button testID="contact-home" label="Back to home" variant="secondary" onClick={() => nav("/")} />
            </div>
          ) : (
            <div className="imm-contact-form" style={{ marginTop: 8 }}>
              <Field testID="contact-name" label="Your name" required value={d.name} onChange={(v) => set("name", v)} placeholder="Full name" />
              <Field testID="contact-email" label="Email" required value={d.email} onChange={(v) => set("email", v)} placeholder="you@example.com" inputMode="email" autoCapitalize="none" />
              <Field testID="contact-phone" label="Mobile number" required value={d.phone} onChange={(v) => set("phone", v)} placeholder="(201) 555-0100" inputMode="tel" />
              <Field testID="contact-message" label="How can we help?" required value={d.message} onChange={(v) => set("message", v)} placeholder="Tell us about your concern…" multiline maxLength={5000} />
              <div aria-live="assertive" role="alert" data-testid="contact-error">{err ? <p className="err-text">{err}</p> : null}</div>
              <Button testID="contact-submit" icon="send" label={busy ? "Sending…" : "Send message"} onClick={submit} loading={busy} disabled={!canSend} />
              <p className="note" style={{ marginTop: 10 }}>Prefer email? Reach us directly at <a href="mailto:support@homecarematches.com">support@homecarematches.com</a>.</p>
            </div>
          )}
        </div>
      </section>
    </ImmersiveShell>
  );
}
